'use client'

import { createContext, useContext, useMemo } from 'react'
import { getCompany } from '../api/auth'
import { resolveCompanyUrl } from '../utils/companyUrl'
import { useQuery } from '@tanstack/react-query'

interface ICompanyFile {
  id: number
  original_filename: string
  stored_filename: string
  file_size: number
  file_path: string
}

interface ICompanyContext {
  code: string
  name: string
  main_color: string
  company_business_registration: ICompanyFile | null
  company_classroom_bg_pc: ICompanyFile | null
  company_classroom_bg_tablet: ICompanyFile | null
  company_logo: ICompanyFile | null
  company_favicon: ICompanyFile | null
  company_og: ICompanyFile | null
  company_contract_status: 'pre' | 'ing' | 'done'
}

const CompanyContext = createContext<ICompanyContext | null>(null)

export const useCompanyContext = () => {
  const context = useContext(CompanyContext)
  if (!context) {
    throw new Error('useCompanyContext must be used within a CompanyProvider')
  }
  return context
}

const CompanyProvider = ({ children }: { children: React.ReactNode }) => {
  // ✅ useMemo로 안정적인 값 생성 (SSR 대응)
  const companyUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : null
    return resolveCompanyUrl(origin)
  }, [])

  const { data, isLoading, error } = useQuery({
    queryKey: ['getCompany', companyUrl],
    queryFn: ({ signal }) => {
      if (!companyUrl) throw new Error('Company URL is not defined')
      return getCompany(companyUrl, signal)
    },
    select: data => data.data,
    enabled: !!companyUrl, // companyUrl이 있을 때만 실행
    // ✅ 중요: 재시도 제한
    retry: 2, // 실패 시 2번만 재시도
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
    // ✅ 캐싱 설정
    staleTime: 5 * 60 * 1000, // 5분간 신선한 데이터로 간주
    gcTime: 10 * 60 * 1000, // 10분간 캐시 유지
    // ✅ 에러 처리
    throwOnError: false,
  })

  // ✅ 에러 로깅 (프로덕션에선 에러 모니터링 서비스로 전송)
  if (error) {
    console.error('[CompanyProvider] Error:', error)
  }

  if (isLoading || !data) {
    return null
  }

  return <CompanyContext.Provider value={data}>{children}</CompanyContext.Provider>
}

export default CompanyProvider
